import React, {
  useMemo,
  Fragment,
} from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useOutletContext } from "react-router-dom";

import { useGetAllManageRefListQuery } from "../../../../reactQueries/AppApi/rqHooksForReferrals";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import { manageReferralEditPath } from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { useTrackActivityMutation } from "../../../../reactQueries/commonRQHooks";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewManageReferralReq = () => {

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { id: userId } = loggedInUser;
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
  const {
    hasA1Access,
  } = clientPortalAdminType

  const canExportXls = isEnterpriseAdminType || hasA1Access;

  /**replace these hardcoded values with real on once admin login feature is implemented */
  // const loggedInUserId = "83556BA4-EFEF-431F-A11A-758FD7899917";

  // console.log("loggedinUser:", userId)

  const {
    isSuccess: isSuccessOnReferralsData,
    isFetching: isFetchingOnReferralsData,
    data: referralsData,
    isError: isErrorOnReferralsData,
  } = useGetAllManageRefListQuery({ loggedInUserId: userId });

  const referralsCMSData = useOutletContext();

  const fetchManageRefReqListLabel = (key) => {
    const labels = referralsCMSData?.pageContent?.ManageReferralRequestList?.Labels;
    return returnLabel(key, labels);
  }

  const fetchManageRefReqListTitle = () => {
    return referralsCMSData?.pageContent?.ManageReferralRequestList?.Title || "";
  };

  const fetchCommonCMSLabel = (key) => {
    const labels = referralsCMSData?.pageContent?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  }

  const referralRequestsTitle =
    fetchManageRefReqListTitle() || "Manage Referral Requests";
  const exportFileName = `${referralRequestsTitle}.xlsx`;
  const exportWorksheetName = referralRequestsTitle;

  const exportColumns = [
    { id: 'documentName', header: fetchManageRefReqListLabel('ManageReferralRequestList.ReferralName'), getValue: (row) => row?.DocumentName || '' },
    { id: 'DocumentStatus', header: fetchManageRefReqListLabel('ManageReferralRequestList.Status'), getValue: (row) => row?.DocumentStatus || '' },
    { id: 'ClientAccountName', header: fetchManageRefReqListLabel('ManageReferralRequestList.ClientAccount'), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'CreatedBy', header: fetchManageRefReqListLabel('ManageReferralRequestList.SubmittedBy'), getValue: (row) => row?.CreatedBy || '' },
    { id: 'submittedOn', header: fetchManageRefReqListLabel('ManageReferralRequestList.SubmittedOn'), getValue: (row) => row?.SubmittedOn ? dateformatConverter(row.SubmittedOn, "DD MMM YYYY hh:mm A") : '' },
    { id: 'assignedOn', header: fetchManageRefReqListLabel('ManageReferralRequestList.AssignedOn '), getValue: (row) => row?.AssignedOn ? dateformatConverter(row.AssignedOn, "DD MMM YYYY hh:mm A") : '' },
  ];

  const columns = useMemo(
    () => [
      {
        id: "documentName",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.ReferralName'), //"Referral Name",
        accessorFn: (originalRow) => {
          const docName = originalRow.DocumentName;

          return docName
        },
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderReferralName") }
      },
      {
        accessorKey: "DocumentStatus",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.Status'), //"Status",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderStatus") }
      },
      {
        accessorKey: "ClientAccountName",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.ClientAccount'), //"Client Account",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderClientAccount") }
      },
      {
        accessorKey: "CreatedBy",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.SubmittedBy'), //"Submitted By",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderSubmittedBy") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(
            originalRow.SubmittedOn,
            "DD MMM YYYY hh:mm A"
          );
          return `${data}`;
        },
        id: "submittedOn",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.SubmittedOn'), //"Submitted On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderSubmittedOn") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(
            originalRow.AssignedOn,
            "DD MMM YYYY hh:mm A"
          );
          return `${data}`;
        },
        id: "assignedOn",
        header: fetchManageRefReqListLabel ('ManageReferralRequestList.AssignedOn '), //"Assigned On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder: fetchManageRefReqListLabel ("ManageReferralRequestList.PlaceholderAssignedOn ") }
      },
    ],
    [referralsCMSData]
  );

  /**redirect hook */
  let navigate = useNavigate();

  return (
    <Fragment>
      {
        !!isFetchingOnReferralsData && fetchCommonCMSLabel('Common.Waitloading') //'Wait loading...'
      }

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {/* Referral requests to manage */}
        {fetchManageRefReqListLabel ("ManageReferralRequestList.ReferralRequestToManage")}
      </Typography>

      {/**incase successful call but empty response */}
      {!!isSuccessOnReferralsData && !referralsData?.length && (<NoTableDataComp />)}


      {!!isSuccessOnReferralsData && !!referralsData?.length && (
        <MaterialReactTable
          columns={columns}
          data={referralsData || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}

          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isFetchingOnReferralsData,
            columnPinning: {
              left: ["mrt-row-actions", "documentName"],
            },
          }}
          enableStickyHeader
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchManageRefReqListLabel("ManageReferralRequestList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchManageRefReqListLabel('ManageReferralRequestList.Actions'), //Actions
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
          }}

          enableRowActions

          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(manageReferralEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )
          }}
        />
      )}

      <MpgErrorDialog
        open={!!isErrorOnReferralsData}
      />
    </Fragment>
  );
};
export default ViewManageReferralReq;
